import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  DollarSign,
  Package,
  Clock,
  Settings,
  Bell,
  Search,
  Menu,
  ChevronDown,
  LogOut,
  User,
  Building2,
  UserCog,
  Shield,
  MessageSquare,
  ShoppingCart,
} from "lucide-react";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UserAvatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/stores/auth.store";
import { useLogout } from "@/hooks/use-auth";
import { usePermissions, PERMISSIONS } from "@/hooks/use-permissions";
import { ChatWidget } from "@/components/chat";
import { Breadcrumbs } from "./breadcrumbs";
import { api } from "@/lib/api";

// Navigation items with required permissions
const tenantNavigationConfig = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    permission: PERMISSIONS.DASHBOARD_VIEW,
  },
  {
    name: "CRM",
    href: "/crm",
    icon: Users,
    permissions: [
      PERMISSIONS.LEADS_VIEW,
      PERMISSIONS.CLIENTS_VIEW,
      PERMISSIONS.CONTACTS_VIEW,
    ],
  },
  {
    name: "Jobs",
    href: "/jobs",
    icon: Briefcase,
    permission: PERMISSIONS.JOBS_VIEW,
  },
  {
    name: "Sales",
    href: "/sales",
    icon: ShoppingCart,
    permissions: [PERMISSIONS.QUOTATIONS_VIEW, PERMISSIONS.SALES_ORDERS_VIEW],
  },
  {
    name: "Invoices",
    href: "/invoices",
    icon: FileText,
    permission: PERMISSIONS.INVOICES_VIEW,
  },
  {
    name: "Products",
    href: "/products",
    icon: Package,
    permission: PERMISSIONS.PRODUCTS_VIEW,
  },
  {
    name: "Finance",
    href: "/finance",
    icon: DollarSign,
    permissions: [
      PERMISSIONS.INVOICES_VIEW,
      PERMISSIONS.PAYMENTS_VIEW,
      PERMISSIONS.EXPENSES_VIEW,
    ],
  },
  {
    name: "Vendors",
    href: "/vendors",
    icon: Users,
    permission: PERMISSIONS.VENDORS_VIEW,
  },
  {
    name: "Attendance",
    href: "/attendance",
    icon: Clock,
    permission: PERMISSIONS.ATTENDANCE_VIEW,
  },
  // {
  //   name: "Reports",
  //   href: "/reports",
  //   icon: FileText,
  //   permission: PERMISSIONS.REPORTS_VIEW,
  // },
  {
    name: "Team",
    href: "/users",
    icon: UserCog,
    permission: PERMISSIONS.USERS_VIEW,
  },
  {
    name: "Settings",
    href: "/settings",
    icon: Settings,
    // Settings is always visible - users can always access their profile
    alwaysVisible: true,
  },
];

// Superadmin navigation
const superadminNavigation = [
  { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
  { name: "Workspaces", href: "/admin/tenants", icon: Building2 },
  {
    name: "Communications",
    href: "/admin/communications",
    icon: MessageSquare,
  },
  { name: "Site Logs", href: "/admin/logs", icon: FileText },
  { name: "Settings", href: "/admin/settings", icon: Settings },
];

interface AttendanceStatus {
  isCheckedIn: boolean;
  attendance?: any;
  checkInTime?: string;
}

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, tenant } = useAuthStore();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const { can, canAny, isSuperUser } = usePermissions();

  // Determine navigation based on user role
  const isSuperAdmin = user?.role === "super_admin";

  // Filter navigation based on permissions
  const navigation = useMemo(() => {
    if (isSuperAdmin) {
      return superadminNavigation;
    }

    // Filter tenant navigation based on user permissions
    return tenantNavigationConfig.filter((item) => {
      // Always visible items (like Settings for profile access)
      if ("alwaysVisible" in item && item.alwaysVisible) return true;

      // Super users can see everything
      if (isSuperUser) return true;

      // Check single permission
      if (item.permission) {
        return can(item.permission);
      }

      // Check multiple permissions (any)
      if (item.permissions) {
        return canAny(item.permissions);
      }

      return true;
    });
  }, [isSuperAdmin, isSuperUser, can, canAny]);

  // Fetch attendance status for non-superadmin users
  const { data: attendanceStatus } = useQuery<AttendanceStatus>({
    queryKey: ["attendance-status"],
    queryFn: async () => {
      const res = await api.get<AttendanceStatus>("/attendance/status");
      return res.data;
    },
    enabled: !isSuperAdmin, // Only fetch for regular users
    refetchInterval: 60000, // Refetch every minute to keep status updated
  });

  // Get display name for header
  const displayName = isSuperAdmin ? "System Administration" : tenant?.name;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-slate-200 transition-all duration-300 lg:translate-x-0",
          sidebarOpen ? "w-64" : "w-20",
          mobileMenuOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-slate-200">
          <button
            onClick={() => navigate(isSuperAdmin ? "/admin" : "/dashboard")}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div
              className={cn(
                "h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0",
                isSuperAdmin ? "bg-amber-500" : "bg-primary",
              )}
            >
              {isSuperAdmin ? (
                <Shield className="h-5 w-5 text-white" />
              ) : (
                <Briefcase className="h-5 w-5 text-white" />
              )}
            </div>
            {sidebarOpen && (
              <div className="flex flex-col text-left">
                <span className="font-semibold text-slate-900">
                  Business OS
                </span>
                <span className="text-xs text-slate-500 truncate max-w-[140px]">
                  {displayName}
                </span>
              </div>
            )}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navigation.map((item, index) => (
            <NavLink
              key={item.name}
              to={item.href}
              end={index === 0} // Only first item (Dashboard) needs exact matching
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-white"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
                )
              }
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              {sidebarOpen && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Sidebar toggle */}
        <div className="hidden lg:flex items-center justify-center p-4 border-t border-slate-200">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-slate-500"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div
        className={cn(
          "transition-all duration-300",
          sidebarOpen ? "lg:pl-64" : "lg:pl-20",
        )}
      >
        {/* Top header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-slate-200 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>

            {/* Search */}
            <div className="hidden sm:block w-64 lg:w-96">
              <Input
                type="search"
                placeholder="Search..."
                leftIcon={<Search className="h-4 w-4" />}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <Button variant="ghost" size="icon" className="text-slate-500">
              <Bell className="h-5 w-5" />
            </Button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 px-2"
                >
                  <div className="relative">
                    <UserAvatar
                      name={`${user?.firstName} ${user?.lastName}`}
                      size="sm"
                    />
                    {/* Attendance status indicator */}
                    {!isSuperAdmin && attendanceStatus && (
                      <span
                        className={cn(
                          "absolute top-0 right-0 h-3 w-3 rounded-full border-2 border-white ring-1 ring-slate-200",
                          attendanceStatus.isCheckedIn
                            ? "bg-green-500"
                            : "bg-red-500",
                        )}
                        title={
                          attendanceStatus.isCheckedIn
                            ? "Checked In"
                            : "Checked Out"
                        }
                      />
                    )}
                  </div>
                  <div className="hidden md:flex flex-col items-start">
                    <span className="text-sm font-medium">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="text-xs text-slate-500 capitalize">
                      {user?.role === "tenant_owner"
                        ? "Workspace Owner"
                        : user?.role === "super_admin"
                        ? "Super Admin"
                        : user?.role?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>My Account</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isSuperAdmin ? (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/tenants")}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Manage Workspaces
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/admin/settings")}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/profile")}
                    >
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/organization")}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Organization
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/team")}
                    >
                      <UserCog className="mr-2 h-4 w-4" />
                      Team
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => logout()}
                  className="text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Breadcrumbs />
          <Outlet />
        </main>
      </div>

      {/* Chat Widget - Only for tenant users */}
      {!isSuperAdmin && <ChatWidget />}
    </div>
  );
}
